import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { api, errDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FileSpreadsheet } from "lucide-react";

const backend = process.env.REACT_APP_BACKEND_URL;
const todayIst = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
const ROW_PAGE = 50;
const isCancelled = (e) => e && (e.code === "ERR_CANCELED" || e.name === "CanceledError" || e.name === "AbortError");

// R15 closure E3 - the DAILY consultation booking view by channel for a product family. Two named populations (newly booked that IST day / scheduled that IST day),
// each reconciled (rows = gross + not-bookings, gross = valid + cancelled, gross = sum of origins), with the rows behind every figure and an export that holds every row.
//
// Request identity (review of f644f9a, finding B): every figure on screen, every drilldown and the export link belong to ONE scope - the date and family the RESPONSE says it is for (`data.date`,
// `data.family.code`), never whatever the inputs say now. Each load / row request carries a generation number; a response (or error) from anything but the latest generation of the SAME panel
// instance is dropped, an in-flight request is aborted when the scope changes or the panel closes / unmounts, and the previous figures are cleared the moment the inputs change so no number is
// ever shown beside a different date.
const ORIGIN_ROWS = [
  ["ai_direct", "AI voice agent (direct)"], ["self_booking", "Customer self-service"], ["employee", "Employee"], ["leadership_admin", "Leadership / admin"],
  ["ai_assisted", "Employee after an AI handoff"], ["unknown_legacy", "Unknown / legacy (no stamped origin)"], ["unrecognised", "Unrecognised origin value"],
];

function Population({ pop, name, onRows }) {
  return (
    <div className="border rounded p-3 space-y-2" data-testid={`daily-pop-${name}`}>
      <p className="text-sm font-semibold">{pop.label}</p>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
        <span>Bookings (gross activity)</span><button className="font-mono text-right underline" data-testid={`daily-${name}-gross`} onClick={() => onRows(name, { group: "gross" }, "All bookings")}>{pop.gross}</button>
        <span>Currently valid</span><button className="font-mono text-right underline" data-testid={`daily-${name}-valid`} onClick={() => onRows(name, { group: "valid" }, "Currently valid bookings")}>{pop.valid}</button>
        <span>Cancelled</span><button className="font-mono text-right underline" data-testid={`daily-${name}-cancelled`} onClick={() => onRows(name, { group: "cancelled" }, "Cancelled bookings")}>{pop.cancelled}</button>
        <span title="Pending (not yet confirmed) or a duplicate the booking gate suppressed: never a booking, nothing was sent">Not bookings (pending / suppressed)</span>
        <button className="font-mono text-right underline" data-testid={`daily-${name}-notbookings`} onClick={() => onRows(name, { group: "not_bookings" }, "Not bookings")}>{pop.not_bookings.total} ({pop.not_bookings.pending} / {pop.not_bookings.suppressed})</button>
      </div>
      <div className="text-xs text-muted-foreground">By booking channel (durable stamped origin only; the buckets add up to the {pop.gross} bookings):</div>
      <div className="space-y-0.5" data-testid={`daily-${name}-origins`}>
        {ORIGIN_ROWS.map(([k, label]) => (
          <div key={k} className="flex justify-between text-sm border rounded px-2 py-0.5">
            <span>{label}</span>
            <button className="font-mono text-xs underline" data-testid={`daily-${name}-origin-${k}`} onClick={() => onRows(name, { origin: k, group: "gross" }, label)}>{pop.origin[k]}</button>
          </div>
        ))}
      </div>
      <p className="text-xs" data-testid={`daily-${name}-channels`}>
        AI <b>{pop.channels.ai_direct}</b> · self-service <b>{pop.channels.self_booking}</b> · employee-made <b>{pop.channels.employee_made.total}</b>
        {" "}(employee {pop.channels.employee_made.employee}, leadership/admin {pop.channels.employee_made.leadership_admin}, AI-assisted {pop.channels.employee_made.ai_assisted})
        · unknown/legacy <b>{pop.channels.unknown_legacy}</b>{pop.channels.unrecognised ? <> · unrecognised <b>{pop.channels.unrecognised}</b></> : null}
      </p>
      <p className="text-xs" data-testid={`daily-${name}-reconciles`}>
        {Object.values(pop.reconciles).every(Boolean) ? "Reconciles: rows = bookings + not-bookings; bookings = valid + cancelled = sum of channels; every appointment counted once." : "DOES NOT RECONCILE - report this."}
      </p>
      {pop.family_scope && (
        <p className="text-xs text-muted-foreground" data-testid={`daily-${name}-outside`}>
          Outside this family (not added above): other products {pop.outside_family.other_products_gross}, no product {pop.outside_family.product_unresolved_gross}; all products {pop.outside_family.all_products_gross}.
        </p>
      )}
    </div>
  );
}

export function DailyBookingsPanel({ products = [] }) {
  const roots = products.filter((p) => !p.parent_code && products.some((c) => c.parent_code === p.code));
  const [date, setDate] = useState(todayIst());
  const [family, setFamily] = useState("SKILL_MIGRATION");
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [rows, setRows] = useState(null);        // { scope, req, ...response } - the rows of ONE exact cell of ONE exact scope
  const [rowsLoading, setRowsLoading] = useState(false);
  const loadGen = useRef(0);
  const loadAbort = useRef(null);
  const rowsGen = useRef(0);
  const rowsAbort = useRef(null);

  const closeRows = useCallback(() => {
    rowsGen.current += 1;
    if (rowsAbort.current) rowsAbort.current.abort();
    rowsAbort.current = null;
    setRows(null); setRowsLoading(false);
  }, []);

  const load = useCallback(() => {
    const gen = ++loadGen.current;
    if (loadAbort.current) loadAbort.current.abort();
    const ctl = new AbortController();
    loadAbort.current = ctl;
    closeRows();
    setData(null); setErr(null);
    api.get(`/reports/daily-bookings?date=${encodeURIComponent(date)}${family ? `&family=${encodeURIComponent(family)}` : ""}`, { signal: ctl.signal })
      .then((r) => { if (gen === loadGen.current) setData(r.data); })
      .catch((e) => { if (gen === loadGen.current && !isCancelled(e)) setErr(errDetail(e, "Could not load the daily booking view")); });
  }, [date, family, closeRows]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => () => {              // unmount: nothing in flight may touch this (gone) panel
    loadGen.current += 1; rowsGen.current += 1;
    if (loadAbort.current) loadAbort.current.abort();
    if (rowsAbort.current) rowsAbort.current.abort();
  }, []);

  // the scope every figure on screen belongs to - taken from the RESPONSE, so the controls can move on without ever relabelling old numbers
  const shown = data ? { date: data.date, family: data.family && data.family.code ? data.family.code : "" } : null;

  const fetchRows = useCallback((scope, req, page) => {
    const gen = ++rowsGen.current;
    if (rowsAbort.current) rowsAbort.current.abort();
    const ctl = new AbortController();
    rowsAbort.current = ctl;
    setRowsLoading(true);
    const qs = new URLSearchParams({ date: scope.date, population: req.population, limit: String(ROW_PAGE), page: String(page), ...(scope.family ? { family: scope.family } : {}), ...req.extra }).toString();
    api.get(`/reports/daily-bookings/drilldown?${qs}`, { signal: ctl.signal })
      .then(({ data: d }) => { if (gen === rowsGen.current) { setRows({ scope, req, ...d }); setRowsLoading(false); } })
      .catch((e) => { if (gen === rowsGen.current && !isCancelled(e)) { setRowsLoading(false); toast.error(errDetail(e, "Could not load the rows")); } });
  }, []);

  const openRows = (population, extra, title) => {
    if (!shown) return;
    fetchRows(shown, { population, extra, title }, 1);
  };

  const exportHref = shown ? `${backend}/api/reports/daily-bookings/export?date=${encodeURIComponent(shown.date)}${shown.family ? `&family=${encodeURIComponent(shown.family)}` : ""}` : null;
  const pages = rows ? Math.max(1, Math.ceil(rows.total / ROW_PAGE)) : 1;
  const inputsMoved = data && (data.date !== date || (data.family && data.family.code ? data.family.code : "") !== family);

  return (
    <Card data-testid="reports-daily-bookings"><CardContent className="pt-4 space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <p className="text-sm font-semibold">Daily consultation bookings by channel</p>
          <p className="text-xs text-muted-foreground">One IST day (00:00 included, next 00:00 excluded). Appointment view - not the Booking Automation lead cohort.</p>
        </div>
        <label className="text-xs">Date (IST)<input type="date" className="block border rounded px-2 py-1 text-sm bg-background" data-testid="daily-date" value={date} onChange={(e) => setDate(e.target.value)} /></label>
        <label className="text-xs">Product family
          <select className="block border rounded px-2 py-1 text-sm bg-background" data-testid="daily-family" value={family} onChange={(e) => setFamily(e.target.value)}>
            <option value="">All products</option>
            {roots.map((p) => <option key={p.code} value={p.code}>{p.name}</option>)}
            {!roots.some((p) => p.code === "SKILL_MIGRATION") && <option value="SKILL_MIGRATION">Skill Migration</option>}
          </select>
        </label>
        {exportHref && (
          <a href={exportHref} target="_blank" rel="noreferrer" data-testid="daily-export-link">
            <Button size="sm" variant="outline" data-testid="daily-export"><FileSpreadsheet className="h-3.5 w-3.5 mr-1.5" />Export every row ({shown.date})</Button>
          </a>
        )}
      </div>
      <p className="text-xs text-muted-foreground" data-testid="daily-scope-note">
        This view counts the WHOLE product family (the root product and every child, e.g. Skill Migration = SKILL_MIGRATION + SM_INHOUSE + SM_MARA) for ONE day of your choice. The Product filter at the top of this page also
        means the whole family by default (choose "This code only" there for the root code alone); the date here is its own and does not follow that page's date range.
      </p>
      {err && <p role="alert" className="text-sm text-destructive" data-testid="daily-error">{err}</p>}
      {!data && !err && <p className="text-xs text-muted-foreground" data-testid="daily-loading">Loading {date}…</p>}
      {data && (
        <>
          <p className="text-xs font-semibold" data-testid="daily-shown-scope">Showing {shown.date} · {data.family.code ? `${data.family.name} family` : "all products"}{inputsMoved ? " (updating…)" : ""}</p>
          <p className="text-xs" data-testid="daily-family-members">
            {data.family.code ? <>Family <b>{data.family.name}</b> = {data.family.members.join(" + ")}</> : "All products"} · overlap between the two lists: {data.overlap.in_both_populations} (distinct appointments in either: {data.overlap.distinct_union})
            · reschedule events that day (not added to any total): {data.reschedule_events_in_window_not_added_to_any_total}
          </p>
          {!data.coverage.complete && <p role="alert" className="text-sm rounded border border-amber-500/60 bg-amber-500/10 px-3 py-2" data-testid="daily-incomplete">{data.coverage.statement}</p>}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <Population pop={data.populations.newly_booked} name="newly_booked" onRows={openRows} />
            <Population pop={data.populations.scheduled} name="scheduled" onRows={openRows} />
          </div>
        </>
      )}
      {(rows || rowsLoading) && (
        <div className="space-y-1" data-testid="daily-rows">
          {rowsLoading && !rows && (
            <div className="flex items-center gap-2">
              <p className="text-xs text-muted-foreground" data-testid="daily-rows-loading">Loading rows…</p>
              <Button size="sm" variant="outline" data-testid="daily-rows-cancel" onClick={closeRows}>Cancel</Button>
            </div>
          )}
          {rows && (
            <>
              <p className="text-sm font-semibold" data-testid="daily-rows-title">
                {rows.req.title} - {rows.req.population === "newly_booked" ? "newly booked" : "scheduled"} on {rows.scope.date}{rows.scope.family ? ` (${rows.scope.family} family)` : ""}: {rows.total} appointment{rows.total === 1 ? "" : "s"}
                {rows.total > 0 ? ` - rows ${(rows.page - 1) * ROW_PAGE + 1}-${(rows.page - 1) * ROW_PAGE + rows.items.length} (page ${rows.page} of ${pages})` : ""}
              </p>
              <div className="overflow-x-auto max-h-72 overflow-y-auto">
                <Table>
                  <TableHeader><TableRow>
                    {["Customer", "Product", "Status", "Channel", "Booked (IST)", "Consultation (IST)", "Lead age at booking (days)"].map((h) => <TableHead key={h}>{h}</TableHead>)}
                  </TableRow></TableHeader>
                  <TableBody>
                    {rows.items.map((r) => (
                      <TableRow key={r.appointment_id} data-testid={`daily-row-${r.appointment_id}`}>
                        <TableCell className="text-xs">{r.customer || "—"}</TableCell><TableCell className="text-xs">{r.product || "—"}</TableCell>
                        <TableCell className="text-xs">{r.status_group}</TableCell><TableCell className="text-xs">{r.booking_origin}</TableCell>
                        <TableCell className="text-xs">{r.booked_at_ist ? r.booked_at_ist.replace("T", " ").slice(0, 16) : "—"}</TableCell>
                        <TableCell className="text-xs">{r.scheduled_start_ist ? r.scheduled_start_ist.replace("T", " ").slice(0, 16) : "—"}</TableCell>
                        <TableCell className="text-xs">{r.lead_age_days_at_booking ?? "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="flex gap-1.5">
                <Button size="sm" variant="outline" data-testid="daily-rows-prev" disabled={rowsLoading || rows.page <= 1} onClick={() => fetchRows(rows.scope, rows.req, rows.page - 1)}>Previous</Button>
                <Button size="sm" variant="outline" data-testid="daily-rows-next" disabled={rowsLoading || !rows.has_more} onClick={() => fetchRows(rows.scope, rows.req, rows.page + 1)}>Next</Button>
                <Button size="sm" variant="outline" data-testid="daily-rows-close" onClick={closeRows}>Close rows</Button>
              </div>
            </>
          )}
        </div>
      )}
    </CardContent></Card>
  );
}
